import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, analysesTable, complianceItemsTable, proposalDraftsTable, companiesTable,
} from "@workspace/db";
import { eq, and, desc } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";

const router = Router();

/** Escape user/AI-controlled values before embedding in HTML to prevent stored XSS. */
function esc(value: string | null | undefined): string {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

router.get("/tenders/:id/report", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params["id"] as string);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company" });

    const [tender, analysis, complianceItems, proposalDraft, company] = await Promise.all([
      db.select().from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
        .limit(1),
      db.select().from(analysesTable)
        .where(eq(analysesTable.tenderId, tenderId))
        .orderBy(desc(analysesTable.createdAt))
        .limit(1),
      db.select().from(complianceItemsTable)
        .where(eq(complianceItemsTable.tenderId, tenderId)),
      db.select().from(proposalDraftsTable)
        .where(eq(proposalDraftsTable.tenderId, tenderId))
        .limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
    ]);

    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const accept = req.headers["accept"] ?? "";
    if (!accept.includes("text/html")) {
      return res.json({
        tender: tender[0],
        analysis: analysis[0] ?? null,
        complianceItems,
        proposalDraft: proposalDraft[0] ?? null,
        company: company[0] ?? null,
      });
    }

    const t = tender[0];
    const a = analysis[0] ?? null;
    const p = proposalDraft[0] ?? null;
    const c = company[0] ?? null;
    const now = new Date().toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
    const nowEn = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    const statusMap: Record<string, string> = {
      bid: "يُوصى بالتقديم / Recommended Bid",
      no_bid: "لا يُوصى بالتقديم / No Bid",
      needs_review: "يحتاج مراجعة / Needs Review",
    };

    const compliantCount = complianceItems.filter((i) => i.status === "ready").length;
    const missingCount = complianceItems.filter((i) => i.status === "missing").length;

    const complianceRows = complianceItems.map((item) => {
      const statusColor = item.status === "ready" ? "#16a34a" : item.status === "missing" ? "#dc2626" : "#ca8a04";
      const statusLabel = item.status === "ready" ? "✓ جاهز / Ready" : item.status === "missing" ? "✗ مفقود / Missing" : "⏳ قيد الانتظار / Pending";
      return `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px">${esc(item.requirement)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;text-transform:capitalize">${esc(item.category)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;color:${statusColor};font-weight:600">${statusLabel}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px">${esc(item.notes)}</td>
      </tr>`;
    }).join("");

    const proposalSections = ([
      ["المقدمة / Introduction", p?.introduction ?? null],
      ["فهم نطاق العمل / Scope Understanding", p?.scopeUnderstanding ?? null],
      ["المنهجية / Methodology", p?.methodology ?? null],
      ["الفريق / Team", p?.team ?? null],
      ["الجدول الزمني / Timeline", p?.timeline ?? null],
      ["إدارة الجودة / Quality Management", p?.qualityManagement ?? null],
      ["إدارة المخاطر / Risk Management", p?.riskManagement ?? null],
      ["الخلاصة / Conclusion", p?.conclusion ?? null],
    ] as Array<[string, string | null]>).filter(([, v]) => !!v);

    const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>تقرير المناقصة / Tender Report – ${esc(t.title)}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Cairo', Arial, sans-serif; background: #f9fafb; color: #111827; direction: rtl; }
    .page { max-width: 900px; margin: 0 auto; background: #fff; padding: 40px; }
    .header { border-bottom: 3px solid #1e40af; padding-bottom: 20px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: flex-start; }
    .logo { font-size: 22px; font-weight: 700; color: #1e40af; }
    .logo-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
    .date { text-align: left; font-size: 12px; color: #6b7280; }
    h1 { font-size: 20px; font-weight: 700; color: #111827; margin-bottom: 4px; }
    h2 { font-size: 15px; font-weight: 700; color: #1e40af; margin: 24px 0 12px; border-right: 4px solid #1e40af; padding-right: 10px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
    .field { background: #f3f4f6; border-radius: 6px; padding: 10px 14px; }
    .field-label { font-size: 11px; color: #6b7280; margin-bottom: 3px; }
    .field-value { font-size: 14px; font-weight: 600; }
    .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 13px; font-weight: 700; }
    .badge-bid { background: #dcfce7; color: #15803d; }
    .badge-nobid { background: #fee2e2; color: #b91c1c; }
    .badge-review { background: #fef3c7; color: #92400e; }
    .section { margin-bottom: 24px; }
    .prose { font-size: 13px; line-height: 1.8; color: #374151; white-space: pre-wrap; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead tr { background: #eff6ff; }
    thead th { padding: 10px 12px; text-align: right; font-weight: 700; font-size: 12px; color: #1e40af; border-bottom: 2px solid #bfdbfe; }
    .summary-bar { display: flex; gap: 16px; margin-bottom: 16px; }
    .summary-item { background: #f3f4f6; border-radius: 6px; padding: 10px 16px; flex: 1; text-align: center; }
    .summary-num { font-size: 24px; font-weight: 700; }
    .summary-lbl { font-size: 11px; color: #6b7280; }
    .proposal-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
    .proposal-section h3 { font-size: 14px; font-weight: 700; color: #1e40af; margin-bottom: 8px; }
    .risks-list { list-style: none; }
    .risks-list li { padding: 6px 10px; border-radius: 4px; margin-bottom: 4px; font-size: 13px; }
    .risk-high { background: #fee2e2; color: #b91c1c; }
    .risk-medium { background: #fef3c7; color: #92400e; }
    .risk-low { background: #f0fdf4; color: #15803d; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; text-align: center; }
    @media print {
      body { background: #fff; }
      .no-print { display: none; }
      .page { padding: 20px; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="no-print" style="position:fixed;top:16px;left:16px;z-index:100">
      <button onclick="window.print()" style="background:#1e40af;color:#fff;border:none;border-radius:6px;padding:8px 20px;font-size:14px;cursor:pointer;font-family:inherit">🖨️ طباعة / Print</button>
    </div>

    <div class="header">
      <div>
        <div class="logo">المساعد الذكي للمناقصات السعودية</div>
        <div class="logo-sub">AI Tender Copilot Saudi</div>
        ${c ? `<div style="font-size:13px;color:#374151;margin-top:6px">${esc(c.name)}</div>` : ""}
      </div>
      <div class="date">
        <div>${esc(now)}</div>
        <div>${esc(nowEn)}</div>
      </div>
    </div>

    <h1>${esc(t.title)}</h1>

    <div class="grid2">
      <div class="field"><div class="field-label">الجهة الحكومية / Entity</div><div class="field-value">${esc(t.governmentEntity) || "—"}</div></div>
      <div class="field"><div class="field-label">رقم المنافسة / Competition No.</div><div class="field-value">${esc(t.competitionNumber) || "—"}</div></div>
      <div class="field"><div class="field-label">القطاع / Sector</div><div class="field-value">${esc(t.sector) || "—"}</div></div>
      <div class="field"><div class="field-label">المدينة / City</div><div class="field-value">${esc(t.city) || "—"}</div></div>
      <div class="field"><div class="field-label">الموعد النهائي / Deadline</div><div class="field-value">${t.deadline ? esc(new Date(t.deadline).toLocaleDateString("ar-SA")) : "—"}</div></div>
      <div class="field">
        <div class="field-label">التوصية / Recommendation</div>
        <div class="field-value">
          ${a ? `<span class="badge ${a.bidDecision === "bid" ? "badge-bid" : a.bidDecision === "no_bid" ? "badge-nobid" : "badge-review"}">${esc(statusMap[a.bidDecision ?? ""] ?? a.bidDecision ?? "—")}</span>` : "—"}
        </div>
      </div>
    </div>

    ${a ? `
    <h2>ملخص التحليل / Analysis Summary</h2>
    <div class="section">
      ${a.executiveSummary ? `<div class="prose">${esc(a.executiveSummary)}</div>` : ""}
      ${a.bidScore != null ? `<div style="margin-top:12px;font-size:13px">درجة الجدوى / Bid Score: <strong>${esc(String(a.bidScore))}/100</strong></div>` : ""}
      ${a.bidReasons && a.bidReasons.length > 0 ? `
      <div style="margin-top:12px">
        <strong style="font-size:13px">أسباب التوصية / Bid Reasons:</strong>
        <ul style="margin:6px 0 0 0;padding-right:20px;font-size:13px;line-height:1.8">
          ${a.bidReasons.map((r) => `<li>${esc(r)}</li>`).join("")}
        </ul>
      </div>` : ""}
      ${a.risks && a.risks.length > 0 ? `
      <div style="margin-top:12px">
        <strong style="font-size:13px">المخاطر / Risks:</strong>
        <ul class="risks-list" style="margin-top:6px">
          ${a.risks.map((r) => `<li class="risk-${esc(r.severity)}">[${esc(r.severity.toUpperCase())}] ${esc(r.title)} – ${esc(r.description)}</li>`).join("")}
        </ul>
      </div>` : ""}
      ${a.requiredDocuments && a.requiredDocuments.length > 0 ? `
      <div style="margin-top:12px">
        <strong style="font-size:13px">المستندات المطلوبة / Required Documents:</strong>
        <ul style="margin:6px 0 0 0;padding-right:20px;font-size:13px;line-height:1.8">
          ${a.requiredDocuments.map((d) => `<li>${esc(d)}</li>`).join("")}
        </ul>
      </div>` : ""}
    </div>
    ` : ""}

    ${complianceItems.length > 0 ? `
    <h2>مصفوفة الامتثال / Compliance Matrix</h2>
    <div class="summary-bar">
      <div class="summary-item"><div class="summary-num" style="color:#15803d">${compliantCount}</div><div class="summary-lbl">جاهز / Ready</div></div>
      <div class="summary-item"><div class="summary-num" style="color:#ca8a04">${complianceItems.length - compliantCount - missingCount}</div><div class="summary-lbl">قيد الانتظار / Pending</div></div>
      <div class="summary-item"><div class="summary-num" style="color:#dc2626">${missingCount}</div><div class="summary-lbl">مفقود / Missing</div></div>
    </div>
    <table>
      <thead><tr>
        <th>المتطلب / Requirement</th><th>الفئة / Category</th><th>الحالة / Status</th><th>ملاحظات / Notes</th>
      </tr></thead>
      <tbody>${complianceRows}</tbody>
    </table>
    ` : ""}

    ${proposalSections.length > 0 ? `
    <h2>مسودة العرض / Proposal Draft</h2>
    ${proposalSections.map(([title, content]) => `
    <div class="proposal-section">
      <h3>${esc(title)}</h3>
      <div class="prose">${esc(content ?? "")}</div>
    </div>`).join("")}
    ` : ""}

    <div class="footer">
      تم إنشاء هذا التقرير تلقائياً بواسطة المساعد الذكي للمناقصات السعودية<br/>
      This report was automatically generated by AI Tender Copilot Saudi
    </div>
  </div>
</body>
</html>`;

    res.setHeader("Content-Type", "text/html; charset=utf-8");
    res.send(html);
  } catch (err) {
    next(err);
  }
});

export default router;
